import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from 'src/components/Button';
import Pagination from 'src/components/Pagination';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import { IconArrowLeftGoals, IconArrowRightGoals, IconSpinner } from 'src/icons';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getPageCount } from 'src/utils/pagination.utils';

import { EmptyDocumentsState } from './EmptyDocumentsState';
import { useGetStudentDocumentsQuery, useSingleStudentYearsFiltersQuery } from './singleStudentApi';
import StudentDocumentsFilters from './StudentDocumentsFilters';
import { StudentDocumentsList } from './StudentDocumentsList';
import { StudentSelectedFiltersDocuments } from './StudentSelectedFiltersDocuments';
import { selectStudentDocumentsFilters } from './StudentSelectors';
import { setStudentDocumentsFilters } from './StudentSlice';

export const StudentDocuments = () => {
  const dispatch = useAppDispatch();
  const { isSm } = useBreakpoint('sm');
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [filtersTriggerOpen, setFiltersTriggerOpen] = useState(false);
  const [_, setIsDefaultYear] = useState(true);
  const [studentYearFilterId, setStudentYearFilterId] = useState<string | undefined>(undefined);

  const filters = useAppSelector(selectStudentDocumentsFilters);

  const { data: studentYears } = useSingleStudentYearsFiltersQuery(id!);

  useEffect(() => {
    if (studentYears && studentYears.length > 0 && !filters.schoolYearId) {
      dispatch(
        setStudentDocumentsFilters({
          ...filters,
          schoolYearId: studentYears[studentYears.length - 1].id,
        }),
      );
      setStudentYearFilterId(studentYears[studentYears.length - 1].id);
    }
  }, [studentYears, filters.schoolYearId, dispatch, filters]);

  const { data: documentsData, isLoading: documentsLoading } = useGetStudentDocumentsQuery(
    {
      studentId: id!,
      from: (page - 1) * 10,
      pageSize: 10,
      filters: filters,
    },
    {
      skip: !id || !filters.schoolYearId,
    },
  );

  const pagesCount = getPageCount(documentsData!);

  useEffect(() => {
    if (studentYears) {
      setCurrentYearIndex(studentYears.length - 1);
      setStudentYearFilterId(studentYears[studentYears.length - 1]?.id);
    }
  }, [studentYears]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const addYear = () => {
    if (studentYears && currentYearIndex + 1 < studentYears?.length) {
      setIsDefaultYear(false);
      setStudentYearFilterId(studentYears[currentYearIndex + 1].id);
      dispatch(
        setStudentDocumentsFilters({
          ...filters,
          schoolYearId: studentYears[currentYearIndex + 1].id,
        }),
      );
      setCurrentYearIndex(currentYearIndex + 1);
      setPage(1);
    }
  };

  const subtractYear = () => {
    if (studentYears && currentYearIndex - 1 >= 0) {
      setIsDefaultYear(false);
      setStudentYearFilterId(studentYears[currentYearIndex - 1].id);
      dispatch(
        setStudentDocumentsFilters({
          ...filters,
          schoolYearId: studentYears[currentYearIndex - 1].id,
        }),
      );
      setCurrentYearIndex(currentYearIndex - 1);
      setPage(1);
    }
  };

  if (documentsLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <IconSpinner className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center">
          <Button
            disabled={currentYearIndex < 1}
            Icon={IconArrowLeftGoals}
            preset="tertiary"
            size="sm"
            className="rounded-e-none"
            onClick={subtractYear}
          />
          <Button
            disabled={studentYears && currentYearIndex >= studentYears.length - 1}
            Icon={IconArrowRightGoals}
            preset="tertiary"
            size="sm"
            className="rounded-s-none"
            onClick={addYear}
          />
          {isSm && (
            <div className="ml-4 flex gap-x-2 typography-heading-lg">
              <div className="hidden xl:block">School Year </div>
              {studentYears && (
                <>
                  {studentYears[currentYearIndex]?.startYear} /{' '}
                  {studentYears[currentYearIndex]?.endYear}
                </>
              )}
            </div>
          )}
        </div>
        {!isSm && (
          <div className="flex gap-x-2 typography-heading-lg">
            {studentYears && (
              <>
                {studentYears[currentYearIndex]?.startYear} /{' '}
                {studentYears[currentYearIndex]?.endYear}
              </>
            )}
          </div>
        )}
        <div className="flex">
          <StudentDocumentsFilters
            studentYearFilterId={studentYearFilterId}
            forceOpen={filtersTriggerOpen}
            onFiltersClosed={() => setFiltersTriggerOpen(false)}
          />
        </div>
      </div>

      <div>
        <StudentSelectedFiltersDocuments />

        {documentsData && documentsData.items.length === 0 ? (
          <EmptyDocumentsState />
        ) : (
          <StudentDocumentsList documents={documentsData?.items || []} />
        )}

        {documentsData && documentsData.items.length > 0 && (
          <div className="flex items-center justify-between sm:mt-8">
            <Pagination
              itemsPerPage={documentsData.pageSize}
              totalSize={documentsData.total}
              pagesCount={pagesCount}
              activePage={page}
              onChangeActivePage={setPage}
              searchingItemsPlaceholder="documents"
            />
          </div>
        )}
      </div>
    </div>
  );
};
